import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type ChangeEvent } from "react";
import { Edit, ImagePlus, Plus, RefreshCw, Search, Sparkles, Trash2, Upload, Workflow } from "lucide-react";
import { toast } from "sonner";
import { AdminTitle, DataTable, Field, Status } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { meta } from "@/components/store/page-parts";
import { useStore, normalizeDbProduct } from "@/lib/store-context";

export const Route = createFileRoute("/admin/products")({
  head: () => meta("Manage Products — BetterAccess Admin", "Create products with artwork, category, description, stock source and highlight badges."),
  component: ProductsAdmin,
});

const highlights = ["None", "Best seller", "Popular", "New", "Limited stock", "Editor's pick"];

function ProductsAdmin() {
  const { categories, refreshProducts } = useStore();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Form states
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState(categories[0]?.slug || "streaming");
  const [deliveryType, setDeliveryType] = useState("instant_code");
  const [priceUsd, setPriceUsd] = useState("4.99");
  const [priceBdt, setPriceBdt] = useState("610");
  const [stockCount, setStockCount] = useState("10");
  const [shortDesc, setShortDesc] = useState("");
  const [desc, setDesc] = useState("");
  const [source, setSource] = useState<"provider" | "own">("own");
  const [providerServiceId, setProviderServiceId] = useState("");
  const [highlight, setHighlight] = useState("None");
  const [featured, setFeatured] = useState(false);
  const [poster, setPoster] = useState("");
  const [posterPreview, setPosterPreview] = useState("");
  const [animation, setAnimation] = useState("");

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/products");
      if (res.ok) {
        const d = await res.json();
        if (Array.isArray(d.products)) setProducts(d.products);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProducts();
  }, []);

  const openNew = () => {
    setEditId(null);
    setName("");
    setSlug("");
    setCategory(categories[0]?.slug || "streaming");
    setDeliveryType("instant_code");
    setPriceUsd("4.99");
    setPriceBdt("610");
    setStockCount("10");
    setShortDesc("");
    setDesc("");
    setSource("own");
    setProviderServiceId("");
    setHighlight("None");
    setFeatured(false);
    setPoster("");
    setPosterPreview("");
    setAnimation("");
    setEditing(true);
  };

  const openEdit = (p: any) => {
    setEditId(p.id);
    setName(p.name || "");
    setSlug(p.slug || "");
    setCategory(p.category_id || p.categorySlug || "streaming");
    setDeliveryType(p.fulfillment_type || "instant_code");
    setPriceUsd(String(p.price_usd || 4.99));
    setPriceBdt(String(p.price_bdt || 610));
    setStockCount(String(p.stock_count || 0));
    setShortDesc(p.short_desc || "");
    setDesc(p.description || "");
    setSource(p.provider_service_id ? "provider" : "own");
    setProviderServiceId(p.provider_service_id || "");
    setHighlight(p.badge || "None");
    setFeatured(Boolean(p.is_featured));
    setPoster(p.image_url || "");
    setPosterPreview(p.image_url || "");
    setAnimation(p.lottie_icon || "");
    setEditing(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a product name");
      return;
    }

    const finalSlug = (slug || name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

    const payload = {
      id: editId || `prod_${Date.now()}`,
      name: name.trim(),
      slug: finalSlug,
      category_id: category,
      fulfillment_type: deliveryType,
      price_usd: Number(priceUsd) || 0,
      price_bdt: Number(priceBdt) || (Number(priceUsd) || 0) * 122,
      stock_count: Number(stockCount) || 0,
      in_stock: Number(stockCount) > 0 || source === "provider" ? 1 : 0,
      short_desc: shortDesc,
      description: desc,
      provider_service_id: source === "provider" ? providerServiceId : null,
      badge: highlight !== "None" ? highlight : null,
      is_featured: featured ? 1 : 0,
      image_url: posterPreview || poster || null,
      lottie_icon: animation || null,
    };

    setSaving(true);
    try {
      const res = await fetch("/api/admin/products/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save product");
      toast.success("Product saved to database successfully!");
      setEditing(false);
      await fetchProducts();
      await refreshProducts();
    } catch (err: any) {
      toast.error(err.message || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    try {
      const res = await fetch(`/api/admin/products/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete product");
      toast.success("Product deleted");
      await fetchProducts();
      await refreshProducts();
    } catch (err: any) {
      toast.error(err.message || "Error deleting product");
    }
  };

  const visible = products.filter(p => `${p.name} ${p.category_id || p.categorySlug}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <AdminTitle
        eyebrow="Catalog"
        title="Products"
        copy="Manage listings, artwork, stock source and the badge shown on the storefront."
        action={
          <Button onClick={editing ? () => setEditing(false) : openNew}>
            <Plus />
            {editing ? "Close editor" : "New product"}
          </Button>
        }
      />

      {editing && (
        <section className="premium-panel mb-6 p-5">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <h2 className="text-lg font-black">{editId ? "Edit product" : "Create product"}</h2>
            <Status>{editId ? "Editing Live" : "Draft"}</Status>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label="Product name">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. ChatGPT Plus"
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="URL Slug">
              <input
                value={slug}
                onChange={e => setSlug(e.target.value)}
                placeholder="e.g. chatgpt-plus"
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Category">
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              >
                {categories.map(c => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Delivery type">
              <select
                value={deliveryType}
                onChange={e => setDeliveryType(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              >
                <option value="instant_code">Instant code</option>
                <option value="instant_link">Instant link</option>
                <option value="account_access">Account access</option>
                <option value="manual_service">Manual service</option>
              </select>
            </Field>
            <Field label="Price in USD ($)">
              <input
                type="number"
                step="0.01"
                value={priceUsd}
                onChange={e => {
                  setPriceUsd(e.target.value);
                  setPriceBdt(String(Math.round((Number(e.target.value) || 0) * 122)));
                }}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Price in BDT (৳)">
              <input
                type="number"
                value={priceBdt}
                onChange={e => setPriceBdt(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Available Stock Count">
              <input
                type="number"
                value={stockCount}
                onChange={e => setStockCount(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Short description">
              <textarea
                rows={2}
                value={shortDesc}
                onChange={e => setShortDesc(e.target.value)}
                placeholder="Brief summary..."
                className="rounded-md border bg-background p-3 font-normal"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Full description">
                <textarea
                  rows={4}
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="Detailed description and inclusions..."
                  className="rounded-md border bg-background p-3 font-normal"
                />
              </Field>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-secondary/40 p-4">
            <p className="text-xs font-extrabold uppercase text-primary">Stock &amp; pricing source</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {(
                [
                  ["provider", "API provider", "Price and quantity sync automatically from Warzone/PrimeFollows."],
                  ["own", "Own stock", "You upload credentials directly to database."],
                ] as const
              ).map(([id, title, copy]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSource(id)}
                  className={`rounded-lg border p-3 text-left transition ${
                    source === id ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/40"
                  }`}
                >
                  <b className="block text-sm">{title}</b>
                  <small className="text-muted-foreground">{copy}</small>
                </button>
              ))}
            </div>
            {source === "provider" && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Provider Service ID">
                  <input
                    value={providerServiceId}
                    onChange={e => setProviderServiceId(e.target.value)}
                    placeholder="e.g. 10482"
                    className="h-10 rounded-md border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
                  />
                </Field>
              </div>
            )}
          </div>

          <div className="mt-6 rounded-lg border bg-secondary/40 p-4">
            <p className="text-xs font-extrabold uppercase text-primary">Storefront highlight</p>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Field label="Badge on product card">
                <select
                  value={highlight}
                  onChange={e => setHighlight(e.target.value)}
                  className="h-10 rounded-md border bg-background px-3 font-normal"
                >
                  {highlights.map(item => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </Field>
              <label className="flex items-center justify-between gap-3 rounded-md border bg-background p-3">
                <span className="min-w-0">
                  <b className="flex items-center gap-1.5 text-sm">
                    <Sparkles className="size-4 text-primary" />
                    Feature on homepage
                  </b>
                  <small className="text-muted-foreground">Shows in the highlighted row above the catalog.</small>
                </span>
                <Switch checked={featured} onCheckedChange={setFeatured} aria-label="Feature on homepage" />
              </label>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <UploadBox
              icon={ImagePlus}
              title="Product image"
              accept="image/png,image/jpeg,image/webp"
              value={poster}
              preview={posterPreview}
              onFile={async file => {
                if (file.size > 2_000_000) {
                  toast.error("Choose an image under 2MB");
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                  setPosterPreview(String(reader.result));
                  setPoster(file.name);
                };
                reader.readAsDataURL(file);
              }}
            />
            <UploadBox
              icon={Workflow}
              title="Animation JSON (optional)"
              accept="application/json,.json"
              value={animation}
              onFile={async file => {
                try {
                  const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
                  setAnimation(file.name);
                  toast.success("Animation JSON loaded");
                } catch {
                  setAnimation("");
                  toast.error("Choose a valid Lottie JSON file");
                }
              }}
            />
          </div>

          <div className="mt-5 grid gap-2 sm:flex sm:justify-end">
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="size-4 animate-spin" /> : "Save to Database"}
            </Button>
          </div>
        </section>
      )}

      <div className="mb-4 flex max-w-md items-center gap-2 rounded-md border bg-card px-3">
        <Search className="size-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-10 flex-1 bg-transparent text-sm outline-none"
          placeholder="Search products"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="border-b bg-secondary/60 text-[10px] font-extrabold uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price USD</th>
              <th className="px-4 py-3">Price BDT</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visible.map(p => (
              <tr key={p.id || p.slug} className="transition hover:bg-secondary/40">
                <td className="px-4 py-3 font-bold">{p.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.category_id || p.categorySlug}</td>
                <td className="px-4 py-3 font-mono">${p.price_usd?.toFixed(2) || "4.99"}</td>
                <td className="px-4 py-3 font-mono">৳{p.price_bdt || 610}</td>
                <td className="px-4 py-3 font-mono">{p.stock_count || 0}</td>
                <td className="px-4 py-3">
                  <Status>{p.in_stock ? "Published" : "Out of stock"}</Status>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}>
                      <Edit className="size-4 text-primary" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)}>
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UploadBox({
  icon: Icon,
  title,
  accept,
  value,
  preview,
  onFile,
}: {
  icon: typeof Upload;
  title: string;
  accept: string;
  value: string;
  preview?: string;
  onFile: (file: File) => Promise<void>;
}) {
  const change = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void onFile(file);
  };
  return (
    <label className="group mx-auto grid aspect-square w-full max-w-72 cursor-pointer place-items-center overflow-hidden rounded-lg border border-dashed bg-secondary/40 p-5 text-center transition hover:border-primary">
      <input className="sr-only" type="file" accept={accept} onChange={change} />
      {preview ? (
        <img src={preview} alt="" className="size-full rounded-md object-cover" />
      ) : (
        <span>
          <Icon className="mx-auto size-7 text-primary" />
          <b className="mt-3 block text-sm">{value || title}</b>
          <small className="mt-1 block text-muted-foreground">{value ? "Validated and ready" : "Square artwork recommended"}</small>
        </span>
      )}
    </label>
  );
}
